import { createSelector } from '@reduxjs/toolkit';
import { groupCredentialsByPhysicalIdentifier } from 'library/helpers/accessControl';
import { selectPeople } from 'reduxStore/people/selectors';
import { selectDevices } from 'reduxStore/devices/selectors';

// 📌 Select All Credentials
export const selectCredentials = createSelector(
	[(state) => state?.Credentials?.data || {}, (state) => state?.Auth?.selectedProfile?.orgID],
	(credentialsData, orgID) => (orgID ? credentialsData[orgID] || [] : [])
);

// 📌 Select Credential By ID
export const selectCredentialByID = createSelector(
	[selectCredentials, (_, credentialID) => credentialID],
	(credentials, credentialID) => {
		return credentials.find((credential) => credential.credentialID === credentialID);
	}
);

// 📌 Select grouped credentials (physical identifier siblings)
export const selectGroupedCredentials = createSelector([selectCredentials], (credentials) =>
	groupCredentialsByPhysicalIdentifier(credentials)
);

// 📌 Select Credentials By Person ID
export const selectCredentialsByPersonID = createSelector(
	[selectCredentials, (_, personID) => personID],
	(credentials, personID) => {
		return credentials.filter((credential) => credential.personID === personID);
	}
);

// 📌 Select All Credential Access Data
export const selectCredentialAccess = createSelector(
	[(state) => state.Credentials.access, (state) => state.Auth.selectedProfile.orgID],
	(accessData, orgID) => accessData[orgID] || []
);

// 📌 Select Credential Access By Person ID
export const selectCredentialAccessByPersonID = createSelector(
	[(state) => state.Credentials.access, (state) => state.Auth.selectedProfile.orgID, (_, personID) => personID],
	(accessData, orgID, personID) => {
		const accessForOrg = accessData[orgID] || [];
		if (!personID) return [];
		return accessForOrg.filter((assignment) => assignment.personID === personID);
	}
);

// 📌 Select Credential Access By Device ID
export const selectCredentialAccessByDeviceID = createSelector(
	[(state) => state.Credentials.access, (state) => state.Auth.selectedProfile.orgID, (_, deviceID) => deviceID],
	(accessData, orgID, deviceID) => {
		const accessForOrg = accessData[orgID] || [];
		if (!deviceID) return [];
		return accessForOrg.filter((assignment) => assignment.deviceID === deviceID);
	}
);

// 📌 Select loading state for a specific Credential ID
export const selectCredentialLoading = (state, credentialID) =>
	state.Credentials.loadingCredentialIDs[credentialID] || null;

// 📌 Select the full loadingCredentialIDs map
export const selectLoadingCredentialIDs = (state) => state.Credentials.loadingCredentialIDs;

// ── Propagation state for the topbar indicator (ported from prd) ────────────
const selectCredentialPropagationMap = (state) => state.Credentials.credentialPropagation;
const selectSiteID = (state) => state?.Auth?.selectedProfile?.siteID;

export const selectPropagationStatus = (state) => state.Credentials.propagationStatus;
export const selectLoadingPropagationStatus = (state) => state.Credentials.loadingPropagationStatus;

// 📌 Site-scoped propagation: filters results by selected siteID
// 'all' or no site → returns everything. Specific site → only results matching that site.
// A result's siteID is the device's OWNER site (floor placement), so also keep results
// whose device is in the current site's device list — devices can be VISIBLE in sites
// other than their owner site, and propagation to them must show wherever they show.
export const selectCredentialPropagationForSite = createSelector(
	[selectCredentialPropagationMap, selectSiteID, selectDevices],
	(propagation, siteID, devices) => {
		if (!siteID || siteID === 'all') return propagation;
		const siteDeviceIDs = new Set(devices.map((d) => d.deviceID));
		const filtered = {};
		for (const [credentialID, entry] of Object.entries(propagation)) {
			const siteResults = entry.results.filter(
				(r) => r.siteID === siteID || r.siteID == null || siteDeviceIDs.has(r.deviceID)
			);
			// Skip credentials with no results for this site (but always include queued entries)
			if (siteResults.length === 0 && entry.results.length > 0 && !entry.queued) continue;
			const succeeded = siteResults.filter((r) => r.status === 'registered').length;
			const failed = siteResults.filter((r) => r.status === 'failed' || r.status === 'skipped').length;
			filtered[credentialID] = {
				...entry,
				results: siteResults,
				current: entry.complete ? siteResults.length : entry.current,
				total: entry.complete ? siteResults.length : entry.total,
				succeeded,
				failed,
				failedDeviceIDs: siteResults
					.filter((r) => r.status === 'failed' || r.status === 'skipped')
					.map((r) => r.deviceID)
			};
		}
		return filtered;
	}
);

// 📌 Select Person assigned to a specific Credential ID
export const selectAssignedPersonByCredentialID = createSelector(
	[
		(state) => state.Credentials.data,
		(state) => state.Auth.selectedProfile.orgID,
		selectPeople,
		(_, credentialID) => credentialID
	],
	(credentialsData, orgID, people, credentialID) => {
		const credentialsForOrg = credentialsData?.[orgID] || [];
		const credential = credentialsForOrg.find(
			(cred) => cred.id === credentialID || cred.credentialID === credentialID
		);

		if (!credential || !credential.personID) return null;

		// Find the person assigned to this credential
		return (
			people.find((person) => person.personID === credential.personID || person.id === credential.personID) ||
			null
		);
	}
);
